import { useRef, useState, useEffect } from "react";
import { motion, useInView, useMotionValue, useSpring, useTransform } from "framer-motion";
import {
    Database,
    Sparkles,
    Boxes,
    Code2,
    Smartphone,
    Server,
    Brain,
    KanbanSquare,
    MessageSquare,
    FileText,
    Building2,
    Receipt,
    UserCog,
    Webhook,
    Terminal,
    Globe2,
} from "lucide-react";

const layers = [
    // {
    //     id: "apps",
    //     title: "Apps",
    //     sub: "Web · iOS · Android · macOS · CLI",
    //     chips: [
    //         { icon: Globe2, label: "Web" },
    //         { icon: Smartphone, label: "iOS" },
    //         { icon: Terminal, label: "CLI" },
    //         { icon: Smartphone, label: "Android" },
    //     ],
    //     accent: "from-emerald-400/30 to-emerald-500/0",
    //     ring: "ring-emerald-400/30",
    //     text: "text-emerald-300",
    // },
    // {
    //     id: "api",
    //     title: "API · SDK",
    //     sub: "Typed REST + GraphQL + Webhooks",
    //     chips: [
    //         { icon: Code2, label: "TypeScript SDK" },
    //         { icon: Webhook, label: "Webhooks" },
    //         { icon: Server, label: "GraphQL" },
    //     ],
    //     accent: "from-violet-500/30 to-violet-500/0",
    //     ring: "ring-violet-400/30",
    //     text: "text-violet-300",
    // },
    {
        id: "modules",
        title: "14 Modules",
        sub: "Projects · CRM · Docs · Chat · HR · Finance",
        chips: [
            { icon: KanbanSquare, label: "Projects" },
            { icon: MessageSquare, label: "Chat" },
            { icon: FileText, label: "Docs" },
            { icon: Building2, label: "CRM" },
            { icon: Receipt, label: "Finance" },
            { icon: UserCog, label: "HR" },
        ],
        accent: "from-cyan-500/30 to-cyan-500/0",
        ring: "ring-cyan-400/30",
        text: "text-cyan-300",
    },
    {
        id: "brain",
        title: "ApexBrain · AI Layer",
        sub: "Agents · Retrieval · Reasoning · Memory",
        chips: [
            { icon: Sparkles, label: "Co‑pilot" },
            { icon: Brain, label: "Reasoning" },
            { icon: Server, label: "Vector DB" },
        ],
        accent: "from-amber-glow/30 to-amber-500/0",
        ring: "ring-amber-glow/30",
        text: "text-amber-glow",
    },
    {
        id: "data",
        title: "Data · Schema · Identity",
        sub: "Single source of truth · RBAC · Audit",
        chips: [
            { icon: Database, label: "Workspace DB" },
            { icon: Boxes, label: "Object store" },
            { icon: Server, label: "Audit logs" },
        ],
        accent: "from-electric/30 to-electric/0",
        ring: "ring-electric/30",
        text: "text-electric",
    },
];

const Layer = ({ layer, index, setHovered }) => {
    const ref = useRef(null);
    const inView = useInView(ref, { once: true, margin: "-80px" });
    const [hasEntered, setHasEntered] = useState(false);
    useEffect(() => {
        if (inView && !hasEntered) {
            const t = setTimeout(() => setHasEntered(true), 700 + index * 100);
            return () => clearTimeout(t);
        }
    }, [inView, hasEntered, index]);

    const depth = (layers.length - 1 - index) * 60;

    return (
        <div
            ref={ref}
            onMouseEnter={() => setHovered(layer.id)}
            className="arch-layer group/layer transition-opacity duration-300"
            data-testid={`arch-layer-${layer.id}`}
        >
            <motion.div
                initial={{ opacity: 0, y: -60, z: depth }}
                animate={
                    inView
                        ? { opacity: 1, y: 0, z: depth }
                        : { opacity: 0, y: -60, z: depth }
                }
                whileHover={{ y: -8 }}
                transition={{
                    duration: hasEntered ? 0.25 : 0.6,
                    delay: hasEntered ? 0 : index * 0.1,
                    ease: [0.16, 1, 0.3, 1],
                }}
                style={{ transformStyle: "preserve-3d" }}
                className="relative rounded-2xl border border-white/10 overflow-hidden backdrop-blur-xl transition-[box-shadow,border-color] duration-300 hover:border-white/20 hover:shadow-[0_0_0_1px_rgba(255,255,255,0.18),0_30px_80px_-20px_rgba(59,130,246,0.4)]"
            >
            {/* Layer gradient bg */}
            <div
                className={`absolute inset-0 bg-gradient-to-br ${layer.accent} opacity-60 pointer-events-none`}
            />
            {/* Inner panel */}
            <div className="relative flex items-center gap-5 sm:gap-8 px-5 sm:px-7 py-5 bg-obsidian-100/85">
                {/* Index pill */}
                <div
                    className={`shrink-0 w-10 h-10 rounded-lg flex items-center justify-center font-code text-xs font-semibold bg-white/[0.04] ring-1 ${layer.ring} ${layer.text}`}
                >
                    L{layers.length - index}
                </div>

                {/* Title + sub */}
                <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                        <h3 className="font-display text-base sm:text-lg font-semibold text-white">
                            {layer.title}
                        </h3>
                        <span
                            className={`font-code text-[10px] uppercase tracking-[0.2em] opacity-0 group-hover/layer:opacity-100 transition-opacity duration-200 ${layer.text}`}
                        >
                            active
                        </span>
                    </div>
                    <p className="text-xs sm:text-sm text-zinc-400 mt-0.5 leading-tight">
                        {layer.sub}
                    </p>
                </div>

                {/* Chips */}
                <div className="hidden md:flex flex-wrap gap-1.5 max-w-md justify-end">
                    {layer.chips.map((c) => (
                        <span
                            key={c.label}
                            className="inline-flex items-center gap-1.5 font-code text-[10px] px-2 py-1 rounded-md bg-white/[0.03] border border-white/[0.08] text-zinc-400 group-hover/layer:text-zinc-200 transition-colors duration-200"
                        >
                            <c.icon className="w-3 h-3" />
                            {c.label}
                        </span>
                    ))}
                </div>
            </div>
            </motion.div>
        </div>
    );
};

export const Architecture = () => {
    const ref = useRef(null);
    const [hovered, setHovered] = useState(null);
    const mx = useMotionValue(0);
    const my = useMotionValue(0);
    const sx = useSpring(mx, { stiffness: 60, damping: 18 });
    const sy = useSpring(my, { stiffness: 60, damping: 18 });
    const rotateY = useTransform(sx, [-0.5, 0.5], [-8, 8]);
    const rotateX = useTransform(sy, [-0.5, 0.5], [4, -4]);

    const onMove = (e) => {
        if (!ref.current) return;
        const rect = ref.current.getBoundingClientRect();
        mx.set((e.clientX - rect.left) / rect.width - 0.5);
        my.set((e.clientY - rect.top) / rect.height - 0.5);
    };
    const onLeave = () => {
        mx.set(0);
        my.set(0);
        setHovered(null);
    };

    return (
        <section
            id="architecture"
            className="relative py-24 sm:py-32 overflow-hidden"
            data-testid="architecture-section"
        >
            {/* Background grid + glow */}
            <div className="absolute inset-0 grid-bg opacity-[0.25] pointer-events-none" />
            <div className="absolute inset-0 radial-glow pointer-events-none" />

            <div className="relative max-w-7xl mx-auto px-6 sm:px-8">
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-12 items-start">
                    {/* Left header */}
                    <div className="lg:col-span-5 lg:sticky lg:top-28">
                        <p className="font-code text-xs uppercase tracking-[0.25em] text-electric font-semibold mb-4">
                            $ tree --infrastructure
                        </p>
                        <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tight text-white leading-[1.05]">
                            One stack.
                            <br />
                            <span className="text-zinc-500">
                                Five composable layers.
                            </span>
                        </h2>
                        <p className="mt-6 text-zinc-400 font-light text-sm sm:text-base leading-relaxed">
                            Every module sits on the same identity, schema and
                            event bus — so AI sees everything, automations fire
                            across modules, and your data never leaves the
                            workspace.
                        </p>

                        {/* Hovered detail */}
                        <div className="mt-7 p-4 rounded-xl border border-white/[0.08] bg-white/[0.02] font-code text-xs">
                            <div className="text-zinc-500 mb-1">
                                {"// hover a layer to inspect"}
                            </div>
                            <div className="text-zinc-200">
                                {hovered
                                    ? `> ${layers.find((l) => l.id === hovered)?.title}`
                                    : "> idle"}
                            </div>
                        </div>

                        <div className="mt-6 flex items-center gap-4 font-code text-[11px] text-zinc-500">
                            <span className="flex items-center gap-1.5">
                                <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse-glow" />
                                99.99% uptime
                            </span>
                            <span className="text-zinc-700">·</span>
                            <span>SOC 2 · ISO 27001</span>
                        </div>
                    </div>

                    {/* Right: 3D layer stack */}
                    <div
                        ref={ref}
                        onMouseMove={onMove}
                        onMouseLeave={onLeave}
                        className="lg:col-span-7 relative"
                        style={{ perspective: "1600px" }}
                    >
                        {/* Vertical connector line */}
                        <div className="absolute left-9 sm:left-12 top-4 bottom-4 w-px bg-gradient-to-b from-emerald-400/40 via-cyan-400/30 to-electric/40 pointer-events-none" />
                        <div className="absolute left-9 sm:left-12 top-4 bottom-4 w-px bg-emerald-400/0 animate-[shimmer_4s_linear_infinite] pointer-events-none"
                             style={{
                                 background: "linear-gradient(180deg, transparent 0%, rgba(94,234,212,0.5) 50%, transparent 100%)",
                                 backgroundSize: "100% 200%",
                             }}
                        />

                        <motion.div
                            style={{ rotateX, rotateY, transformStyle: "preserve-3d" }}
                            className="space-y-4 sm:space-y-5 has-[.arch-layer:hover]:[.arch-layer]:opacity-40 [.arch-layer:hover]:!opacity-100"
                        >
                            {layers.map((l, i) => (
                                <Layer
                                    key={l.id}
                                    layer={l}
                                    index={i}
                                    setHovered={setHovered}
                                />
                            ))}
                        </motion.div>
                    </div>
                </div>
            </div>
        </section>
    );
};
